'use client'

import { cn } from '@mntn-dev/ui-utilities'
import type { PaginatedResult } from '@mntn-dev/utility-types'
import { useMemo } from 'react'

import { themeBorderColorMap } from '@mntn-dev/ui-theme'
import { Button } from '../buttons/button'

export interface PaginationToolbarProps {
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  goToPage: (page: number) => void
  goToNextPage: () => void
  goToPreviousPage: () => void
  className?: string
  maxVisiblePages?: number
}

export const PaginationToolbar = ({
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  goToPage,
  goToNextPage,
  goToPreviousPage,
  className,
  maxVisiblePages = 7,
}: PaginationToolbarProps) => {
  const pageNumbers = useMemo(() => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const result: (
      | number
      | { type: 'ellipsis'; position: 'before' | 'after' }
    )[] = []

    // Named constants for pagination logic
    const SIDE_COUNT = maxVisiblePages - 2 // Number of slots for pages (excluding first and last page) in edge scenarios
    const MIDDLE_COUNT = maxVisiblePages - 4 // Number of page slots when both ellipses are used
    const SURROUNDING_COUNT = Math.floor(MIDDLE_COUNT / 2) // Pages to show on either side of the current page in the middle scenario

    // Always show the first page
    result.push(1)

    if (currentPage <= SURROUNDING_COUNT + 2) {
      // Near the beginning: show the first block of pages
      for (let i = 2; i <= SIDE_COUNT; i++) {
        if (i < totalPages) {
          result.push(i)
        }
      }
      if (totalPages > maxVisiblePages) {
        result.push({ type: 'ellipsis', position: 'after' })
      }
    } else if (currentPage >= totalPages - (SURROUNDING_COUNT + 1)) {
      // Near the end: show the last block of pages
      if (totalPages > maxVisiblePages) {
        result.push({ type: 'ellipsis', position: 'before' })
      }
      for (let i = totalPages - SIDE_COUNT + 1; i < totalPages; i++) {
        if (i > 1) {
          result.push(i)
        }
      }
    } else {
      // In the middle: show pages surrounding the current page
      result.push({ type: 'ellipsis', position: 'before' })
      for (
        let i = currentPage - SURROUNDING_COUNT;
        i <= currentPage + SURROUNDING_COUNT;
        i++
      ) {
        result.push(i)
      }
      result.push({ type: 'ellipsis', position: 'after' })
    }

    // Always add the last page if not already included
    if (totalPages > 1 && result[result.length - 1] !== totalPages) {
      result.push(totalPages)
    }

    return result
  }, [currentPage, totalPages, maxVisiblePages])

  // Modified: Always show pagination UI even with only one page
  const pageNumbersToDisplay = totalPages <= 1 ? [1] : pageNumbers

  return (
    <div
      className={cn(
        'flex items-center justify-between w-full relative border-t',
        themeBorderColorMap.muted,
        className
      )}
    >
      {/* Previous button */}
      <Button
        variant="text"
        size="sm"
        iconLeft="ArrowLeftIcon"
        onClick={goToPreviousPage}
        disabled={!hasPreviousPage || totalPages <= 1}
        aria-label="Previous page"
        className="pt-4 z-10"
        textColor="primary"
      >
        Previous
      </Button>

      {/* Page numbers */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-6 pt-4">
          {pageNumbersToDisplay.map((page, index) => {
            if (typeof page !== 'number') {
              // Ellipsis
              return (
                <span
                  key={`ellipsis-${page.position}-${index}`}
                  className="text-gray-400"
                >
                  ...
                </span>
              )
            }

            const isActive = currentPage === page

            return (
              <div key={`page-${page}`} className="relative">
                {/* Highlight line for active page */}
                {isActive && (
                  <div className="absolute -top-4 left-0 right-0 h-[2px] bg-primary-blue" />
                )}
                <button
                  type="button"
                  onClick={() => goToPage(page)}
                  aria-label={`Go to page ${page}`}
                  {...(isActive ? { 'aria-current': 'page' } : {})}
                  className={cn(
                    'w-6 text-center',
                    isActive ? 'text-primary-blue font-medium' : 'text-gray-400'
                  )}
                >
                  {page}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Next button */}
      <Button
        variant="text"
        size="sm"
        iconRight="ArrowRightIcon"
        onClick={goToNextPage}
        disabled={!hasNextPage || totalPages <= 1}
        aria-label="Next page"
        className="pt-4 z-10"
        textColor="primary"
      >
        Next
      </Button>
    </div>
  )
}

// Helper hook to work with PaginationToolbar
export function usePaginationToolbar<T>(
  paginationResult: Pick<
    PaginatedResult<T>,
    'currentPage' | 'totalPages' | 'hasNextPage' | 'hasPreviousPage'
  > & {
    goToPage: (page: number) => void
    goToNextPage: () => void
    goToPreviousPage: () => void
  }
) {
  return {
    paginationProps: {
      currentPage: paginationResult.currentPage,
      totalPages: paginationResult.totalPages,
      hasNextPage: paginationResult.hasNextPage,
      hasPreviousPage: paginationResult.hasPreviousPage,
      goToPage: paginationResult.goToPage,
      goToNextPage: paginationResult.goToNextPage,
      goToPreviousPage: paginationResult.goToPreviousPage,
    } as PaginationToolbarProps,
  }
}
